import React, { useEffect, useMemo, useRef } from "react";
import { CheckCircle2, Printer, Calendar, ShieldCheck } from "lucide-react";
import { playWaiverChime } from "@/lib/chime";

/**
 * Big green WAIVER SIGNED ✓ confirmation screen that the player shows to
 * the front-desk. Designed to be unambiguous from across the desk:
 *   • Huge name in bold
 *   • Today's date with a green stripe (or red if stale)
 *   • Full liability waiver text (for printable PDF receipt — uses window.print())
 *   • Profile picture (Google avatar) if available
 *   • A short "proof code" derived from the waiver hash so staff can spot-check
 */
const WaiverSuccess = ({ user, waiverText, signedAt, signatureName, parentGuardianName }) => {
  const playedOnce = useRef(false);

  useEffect(() => {
    if (!playedOnce.current) {
      playedOnce.current = true;
      // 1 frame delay so the screen transition + render finishes first
      window.requestAnimationFrame(() => playWaiverChime());
    }
  }, []);

  const signedDate = useMemo(() => {
    if (!signedAt) return null;
    try { return new Date(signedAt); } catch { return null; }
  }, [signedAt]);

  const displaySignedDate = signedDate || new Date();

  const isToday = useMemo(() => {
    if (!signedDate) return true; // fresh sign — treat as today
    const n = new Date();
    return n.toDateString() === signedDate.toDateString();
  }, [signedDate]);

  // Short, deterministic 6-char "proof code" derived from the signed date string + user id.
  // Pure: same inputs always yield the same code. Not security-critical — visual spot-check only.
  const proofCode = useMemo(() => {
    const datePart = signedDate ? signedDate.toISOString().slice(0, 10) : "today";
    const seed = `${user?.user_id || user?.email || "anon"}|${datePart}`;
    let hash = 5381;
    for (let i = 0; i < seed.length; i += 1) {
      hash = ((hash << 5) + hash + seed.charCodeAt(i)) | 0;
    }
    return Math.abs(hash).toString(36).slice(0, 6).toUpperCase().padEnd(6, "X");
  }, [user, signedDate]);

  const displayName = signatureName || user?.name || user?.email || "—";

  return (
    <div className="min-h-[calc(100vh-72px)] bg-emerald-500 text-black flex items-stretch" data-testid="waiver-success-screen">
      <div className="w-full max-w-4xl mx-auto p-6 lg:p-10 flex flex-col">
        {/* Top stripe — easy visual confirmation for front desk */}
        <div
          className={`rounded-md font-orbitron font-black uppercase tracking-[0.3em] text-xs text-center py-2 ${
            isToday ? "bg-black/90 text-emerald-400" : "bg-red-600 text-white"
          }`}
          data-testid="waiver-success-stripe"
        >
          {isToday ? "Signed today · valid for entry" : "Signed earlier — confirm date below"}
        </div>

        {/* Main confirmation card */}
        <div className="mt-6 flex-1 bg-white border-4 border-black/90 rounded-lg shadow-2xl p-6 lg:p-10 flex flex-col items-center text-center print:shadow-none print:border-0 print:p-0">
          <CheckCircle2 className="w-24 h-24 lg:w-28 lg:h-28 text-emerald-600" strokeWidth={2.2} />
          <p className="mt-4 font-orbitron text-xs font-bold uppercase tracking-[0.45em] text-emerald-700">
            Liability Waiver
          </p>
          <h1 className="mt-2 font-orbitron text-4xl sm:text-5xl lg:text-6xl font-black uppercase text-black leading-tight" data-testid="waiver-success-title">
            Signed ✓
          </h1>

          {/* Player block — huge name so front desk can read it from across the room */}
          <div className="mt-8 flex flex-col items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-20 h-20 rounded-full ring-4 ring-emerald-500/40 object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center text-3xl font-orbitron font-bold text-emerald-700">
                {(displayName?.[0] || "?").toUpperCase()}
              </div>
            )}
            <p className="font-orbitron text-3xl sm:text-4xl lg:text-5xl font-black text-black uppercase tracking-tight leading-none" data-testid="waiver-success-name">
              {displayName}
            </p>
            {parentGuardianName ? (
              <p className="text-sm font-exo text-black/70">
                Guardian: <span className="font-semibold">{parentGuardianName}</span>
              </p>
            ) : null}
          </div>

          {/* Meta row */}
          <div className="mt-8 grid grid-cols-2 gap-4 w-full max-w-lg">
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-left">
              <div className="text-[0.65rem] font-orbitron uppercase tracking-[0.25em] text-emerald-700 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Signed at</div>
              <div className="mt-1 font-orbitron text-lg font-black text-black" data-testid="waiver-success-time">
                {displaySignedDate.toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" })}
              </div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-left">
              <div className="text-[0.65rem] font-orbitron uppercase tracking-[0.25em] text-emerald-700 flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Proof code</div>
              <div className="mt-1 font-orbitron text-lg font-black text-black tracking-[0.3em]" data-testid="waiver-success-proof-code">
                {proofCode}
              </div>
            </div>
          </div>

          <p className="mt-8 font-exo text-base sm:text-lg text-black/75 max-w-md print:hidden">
            Please show this screen to a Spike Sports staff member at the front desk to confirm your waiver.
          </p>

          <button
            type="button"
            data-testid="waiver-success-print-btn"
            onClick={() => window.print()}
            className="mt-6 inline-flex items-center gap-2 bg-black hover:bg-black/85 text-white font-orbitron text-xs font-bold uppercase tracking-[0.2em] px-5 py-3 rounded-sm transition-colors print:hidden"
          >
            <Printer className="w-4 h-4" /> Download / Print Receipt (PDF)
          </button>

          {/* Full waiver body — only shown in print, hidden on screen */}
          <div className="hidden print:block mt-10 w-full text-left">
            <h2 className="font-orbitron text-lg font-bold uppercase tracking-wider">Spike Sports — Liability Waiver</h2>
            <p className="mt-1 text-xs">
              Signed by <strong>{displayName}</strong> on{" "}
              {displaySignedDate.toLocaleString("en-CA", { dateStyle: "long", timeStyle: "short" })}.
              {parentGuardianName ? <> Guardian: <strong>{parentGuardianName}</strong>.</> : null}
              {" "}Proof code: <strong>{proofCode}</strong>.
            </p>
            <pre className="mt-4 whitespace-pre-wrap text-[10px] leading-relaxed font-exo">
              {waiverText || "(Waiver text unavailable)"}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaiverSuccess;
